import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { USERS } from "@/constants/testIds";
import {
  Plus,
  Trash2,
  KeyRound,
  AtSign,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
  ShieldOff,
  UserCog,
} from "lucide-react";

import {
  UserFormModal,
  ResetPasswordModal,
  ChangeEmailModal,
  ConfirmModal,
} from "./users-admin/UsersAdminModals";
import GreetingTemplateSection from "./users-admin/GreetingTemplateSection";
import ReminderSettingsSection from "./users-admin/ReminderSettingsSection";
import EmailTemplatesSection from "./users-admin/EmailTemplatesSection";
import MailtoLogSection from "./users-admin/MailtoLogSection";
import LoginLogSection from "./users-admin/LoginLogSection";
import PastoralStatsSection from "./users-admin/PastoralStatsSection";

const ROLE_LABEL = {
  administrador: "Administrador",
  secretaria: "Secretaría",
};

const ROLE_COLOR = {
  administrador: "#7a5b0f",
  secretaria: "#1c2a3a",
};

export default function UsersAdmin() {
  const { user: me, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pinTarget, setPinTarget] = useState(null);

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, []);

  const flash = (msg) => {
    setInfo(msg);
    setError("");
    setTimeout(() => setInfo(""), 2500);
  };

  const createUser = async (payload) => {
    try {
      await api.post("/users", payload);
      setShowForm(false);
      flash("Usuario creado");
      await load();
    } catch (e) {
      throw e?.response?.data?.detail || "Error al crear usuario";
    }
  };

  const resetPassword = async (id, password) => {
    try {
      await api.put(`/users/${id}`, { password });
      setResetTarget(null);
      flash("Contraseña actualizada");
    } catch (e) {
      throw e?.response?.data?.detail || "Error al actualizar contraseña";
    }
  };

  const changeEmail = async (id, email, isSelf) => {
    try {
      await api.put(`/users/${id}`, { email });
      setEmailTarget(null);
      if (isSelf) {
        // The session token still carries the old email — force a clean re-login
        await logout();
        return;
      }
      flash("Correo actualizado");
      await load();
    } catch (e) {
      throw e?.response?.data?.detail || "Error al actualizar correo";
    }
  };

  const doResetPin = async () => {
    if (!pinTarget) return;
    try {
      await api.delete(`/users/${pinTarget.id}/pin`);
      setPinTarget(null);
      flash("PIN eliminado — el usuario ya puede entrar solo con su contraseña");
      await load();
    } catch (e) {
      setPinTarget(null);
      setError(e?.response?.data?.detail || "Error al quitar el PIN");
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      flash("Usuario eliminado");
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div data-testid={USERS.page}>
      <PageHeader
        title="Gestión de Usuarios"
        subtitle="Cuentas con acceso al sistema · Administrador / Secretaría"
      />

      <div className="px-8 pt-6">
        <PastoralStatsSection />

        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e]">
            {users.length} usuario(s)
          </div>
          <button
            data-testid={USERS.addButton}
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded border border-[#1c2a3a] bg-[#1c2a3a] px-3 py-2 text-[11px] uppercase tracking-widest text-white hover:bg-[#0f1a2a]"
          >
            <Plus size={14} /> Nuevo usuario
          </button>
        </div>

        {error && (
          <div
            data-testid={USERS.errorMsg}
            className="mb-3 flex items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700"
          >
            <AlertCircle size={14} /> {error}
          </div>
        )}
        {info && !error && (
          <div
            data-testid={USERS.infoMsg}
            className="mb-3 flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700"
          >
            <CheckCircle2 size={14} /> {info}
          </div>
        )}

        <div className="overflow-x-auto border border-[#d9cfb8] bg-white shadow-sm">
          <table data-testid={USERS.table} className="w-full text-[13px]">
            <thead className="bg-[#1c2a3a] text-white">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">
                  Correo
                </th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">
                  Rol
                </th>
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-medium">
                  Creado
                </th>
                <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest font-medium">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-[#8b7e5e]">
                    Cargando...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-[#8b7e5e]">
                    No hay usuarios.
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const isSelf = me?.email?.toLowerCase() === u.email.toLowerCase();
                  return (
                    <tr
                      key={u.id}
                      data-testid={USERS.row(u.id)}
                      className="border-t border-[#e8e1cd] hover:bg-[#fbf7ee]"
                    >
                      <td className="px-3 py-2 text-[#1c1a14]">
                        {u.email}
                        {isSelf && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-[#8b7e5e]">
                            (tú)
                          </span>
                        )}
                        {u.has_pin && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 rounded border border-[#c8a04a] px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#7a5b0f]"
                            title="2FA con PIN activado"
                          >
                            <ShieldCheck size={10} /> PIN
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest text-white"
                          style={{ background: ROLE_COLOR[u.role] || "#8b7e5e" }}
                        >
                          {u.role === "administrador" ? (
                            <ShieldCheck size={11} />
                          ) : (
                            <UserCog size={11} />
                          )}
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-[#5d5849]">
                        {(u.created_at || "").slice(0, 10) || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="inline-flex items-center justify-center gap-3 w-full">
                          <button
                            data-testid={USERS.changeEmailButton(u.id)}
                            onClick={() => setEmailTarget(u)}
                            className="text-[#1c2a3a] hover:text-[#c8a04a]"
                            title="Cambiar correo"
                          >
                            <AtSign size={14} />
                          </button>
                          <button
                            data-testid={USERS.resetPwdButton(u.id)}
                            onClick={() => setResetTarget(u)}
                            className="text-[#1c2a3a] hover:text-[#c8a04a]"
                            title="Restablecer contraseña"
                          >
                            <KeyRound size={14} />
                          </button>
                          <button
                            data-testid={USERS.resetPinButton(u.id)}
                            onClick={() => setPinTarget(u)}
                            disabled={!u.has_pin}
                            className={`${
                              u.has_pin
                                ? "text-[#1c2a3a] hover:text-[#c8a04a]"
                                : "text-[#d4cfbe] cursor-not-allowed"
                            }`}
                            title={
                              u.has_pin
                                ? "Quitar PIN (2FA) — si el usuario lo olvidó"
                                : "Sin PIN configurado"
                            }
                          >
                            <ShieldOff size={14} />
                          </button>
                          <button
                            data-testid={USERS.deleteButton(u.id)}
                            onClick={() => setDeleteTarget(u)}
                            disabled={isSelf}
                            className={`${
                              isSelf
                                ? "text-[#d4cfbe] cursor-not-allowed"
                                : "text-[#9b2c2c] hover:text-[#6e1f1f]"
                            }`}
                            title={isSelf ? "No puede eliminarse a sí mismo" : "Eliminar"}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <GreetingTemplateSection />
        <ReminderSettingsSection />
        <EmailTemplatesSection />
        <MailtoLogSection />
        <LoginLogSection />
      </div>

      {showForm && (
        <UserFormModal
          onClose={() => setShowForm(false)}
          onSubmit={createUser}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          onClose={() => setResetTarget(null)}
          onSubmit={(pwd) => resetPassword(resetTarget.id, pwd)}
        />
      )}
      {emailTarget && (
        <ChangeEmailModal
          user={emailTarget}
          isSelf={me?.email?.toLowerCase() === emailTarget.email.toLowerCase()}
          onClose={() => setEmailTarget(null)}
          onSubmit={(email) =>
            changeEmail(
              emailTarget.id,
              email,
              me?.email?.toLowerCase() === emailTarget.email.toLowerCase(),
            )
          }
        />
      )}
      {pinTarget && (
        <ConfirmModal
          title="Quitar PIN de seguridad"
          message={`Se eliminará el PIN (2FA) de ${pinTarget.email}. Podrá iniciar sesión solo con su contraseña y configurar un PIN nuevo.`}
          confirmLabel="Quitar PIN"
          onCancel={() => setPinTarget(null)}
          onConfirm={doResetPin}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title={`Eliminar "${deleteTarget.email}"`}
          message="Se eliminará la cuenta. Esta acción no se puede deshacer."
          onCancel={() => setDeleteTarget(null)}
          onConfirm={doDelete}
        />
      )}
    </div>
  );
}
